import useElevarEvents from "@hooks/useElevarEvents"
import useEnhancedEcommerceEvents from "@hooks/useEnhancedEcommerceEvents"
import useMaestraEvents from "@hooks/useMaestraEvents"
import { InventoryData } from "@hooks/useMultiWarehouseInventory"
import { findBestCollectionForPage } from "@utils/findBestCollectionForPage"
import React from "react"

type Args = {
  customerEmail?: string
  formattedProducts: any[]
  inventoryData: InventoryData
  location: { pathname: string }
  pageContext: {
    seoHeader?: string
    seoTitle?: string
    title?: string
  }
}

/**
 * Handles analytics events for collection pages:
 * - GTM view product list
 * - Elevar view item list
 * - Maestra view category (with inferred Shopify collection ID)
 */
export function useCollectionAnalytics({
  customerEmail,
  formattedProducts,
  inventoryData,
  location,
  pageContext,
}: Args) {
  const { onViewProductList } = useEnhancedEcommerceEvents()
  const { elevarOnViewItemList } = useElevarEvents()
  const { maestraOnViewCategory } = useMaestraEvents()

  const [onViewProductListCalled, setOnViewProductListCalled] =
    React.useState<boolean>(false)

  // Infer Shopify collection ID for Maestra
  const [maestraCategoryId, setMaestraCategoryId] = React.useState<string>("")
  const [maestraCategoryIdResolved, setMaestraCategoryIdResolved] =
    React.useState<boolean>(false)

  React.useEffect(() => {
    if (inventoryData && Object.keys(inventoryData).length > 0) {
      const pageTitle =
        pageContext.seoHeader || pageContext.seoTitle || pageContext.title
      const collectionId = findBestCollectionForPage(
        pageTitle || "",
        inventoryData
      )
      setMaestraCategoryId(collectionId || location.pathname)
      setMaestraCategoryIdResolved(true)
    }
  }, [inventoryData, pageContext, location.pathname])

  React.useEffect(() => {
    if (
      !onViewProductListCalled &&
      formattedProducts[0] &&
      maestraCategoryIdResolved
    ) {
      const gtmProducts = formattedProducts.map(({ gtmProduct }) => gtmProduct)
      const elevarProducts = formattedProducts.map(
        ({ elevarProduct }) => elevarProduct
      )
      onViewProductList(gtmProducts)
      elevarOnViewItemList(elevarProducts)
      maestraOnViewCategory({
        category: maestraCategoryId,
        user: { email: customerEmail || "" },
      })
      setOnViewProductListCalled(true)
    }
  }, [
    formattedProducts,
    maestraCategoryId,
    maestraCategoryIdResolved,
    onViewProductListCalled,
    customerEmail,
    onViewProductList,
    elevarOnViewItemList,
    maestraOnViewCategory,
  ])
}
